import { useMutation } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { useApiDownload, useApiDownloadPost, useApiQuery } from 'src/api';
import { useBuildVars } from 'src/config';
import { SearchSettings } from '../advanceSearch/InterfaceTypes';
import dayjs from 'dayjs';
import {
  useFiltersItems,
  useFilterSettings,
  useDefaultOperators,
  useSubscriptionFilterSettings,
  useSubscriptionFiltersItems,
} from './advancedSearchOptions';

export {
  useFiltersItems,
  useFilterSettings,
  useDefaultOperators,
  useSubscriptionFilterSettings,
  useSubscriptionFiltersItems,
};

export type RecordResponse = {
  deal_display_number: string;
  deal_expiry: string;
  deal_id: string | null;
  deal_ingested_on: string;
  deal_number: string;
  deal_start: string;
  enduser_name: string;
  euaffiliationcount: number;
  id: number;
  importreferenceid: number;
  price_protection_expiry: string;
  reseller_name: string;
  vendor_name: string;
  vendor_number: string;
  vendor_program_id: number;
  vendor_program_name: string;
  version_number: string;
};

export type AdvancesdSearchResponse = {
  deals: RecordResponse[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
};

export type PageRequest = {
  page: number;
  pageSize: number;
};

export type AdvancesdSearchResquest = {
  countryCode: string;
  dealNumber: string;
  dealNumberContains: string;
  dealNumberExcludes: string;
  dealNumberPrefix: string;
  discountLineDetailProductGroup: string;
  discountLineDetailProductGroupContains: string;
  discountLineDetailProductGroupExcludes: string;
  discountLineDetailText1: string;
  discountLineDetailText1Contains: string;
  discountLineDetailText1Excludes: string;
  discountLineDetailText1Prefix: string;
  discountLineDetailText2: string;
  discountLineDetailText2Contains: string;
  discountLineDetailText2Excludes: string;
  discountLineDetailText2Prefix: string;
  discountLineDetailText3: string;
  discountLineDetailText3Contains: string;
  discountLineDetailText3Excludes: string;
  discountLineDetailText3Prefix: string;
  discountLineDetailText4: string;
  discountLineDetailText4Contains: string;
  discountLineDetailText4Excludes: string;
  discountLineDetailText4Prefix: string;
  discountLineDetailVPN: string;
  discountLineDetailVPNContains: string;
  discountLineDetailVPNExcludes: string;
  discountLineDetailVPNPrefix: string;
  discountLineDetailVendorNumber: string;
  discountLineDetailVendorNumberContains: string;
  discountLineDetailVendorNumberExcludes: string;
  discountLineDetailVendorNumberPrefix: string;
  effectiveDate: string;
  endUserName: string;
  endUserNameContains: string;
  endUserNameExcludes: string;
  endUserNamePrefix: string;
  endUserNumber: string;
  endUserNumberContains: string;
  endUserNumberExcludes: string;
  endUserNumberPrefix: string;
  expirationDate: string;
  masterContract: string;
  masterContractContains: string;
  masterContractExcludes: string;
  masterContractPrefix: string;
  maxEffectiveDate: string;
  maxExpirationDate: string;
  maxPriceProtectionDate: string;
  minEffectiveDate: string;
  minExpirationDate: string;
  minPriceProtectionDate: string;
  orderable: string;
  priceProtectionDate: string;
  programName: string;
  programNameContains: string;
  programNameExcludes: string;
  programNamePrefix: string;
  resellerName: string;
  resellerNameContains: string;
  resellerNameExcludes: string;
  resellerNamePrefix: string;
  resellerNumber: string;
  resellerNumberContains: string;
  resellerNumberExcludes: string;
  resellerNumberPrefix: string;
  searchType: 'Simple' | 'Advanced';
  vendorName: string;
  vendorNameContains: string;
  vendorNameExcludes: string;
  vendorNamePrefix: string;
  vendorNumber: string;
  vendorNumberContains: string;
  vendorNumberExcludes: string;
  vendorNumberPrefix: string;
};

export function useAdvancedSearchQuery(
  params: Partial<AdvancesdSearchResquest> & PageRequest
) {
  const buildVars = useBuildVars();
  return useApiQuery<AdvancesdSearchResponse>(
    '/system.webapi.gds/api/v1/GdsCoreApiDeals/advancedSearch',
    { ...params },
    undefined,
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export function useDealAdvanceSearchDownloadFile(
  params: Partial<AdvancesdSearchResquest>
) {
  const buildVars = useBuildVars();
  const apiGetDownload = useApiDownload({ host: buildVars.VP_API_BASE });
  return useMutation(() =>
    apiGetDownload(
      '/system.webapi.gds/api/v1/GdsCoreApiDeals/advancedSearch.xlsx',
      params
    )
  );
}

export function useVendorSearchDownloadFile(params: { vendorName: string }) {
  const buildVars = useBuildVars();
  const apiPostDownload = useApiDownloadPost({
    host: buildVars.VP_API_BASE,
  });
  return useMutation(() =>
    apiPostDownload(
      '/system.webapi.gds/api/v1/gdsCoreApiVendor/ExportVendorProgramDetails',
      params,
      undefined,
      []
    )
  );
}

export interface RequestGeneratorOptions {
  businessType: string;
  countryCode: string | null;
  dealNumber: string | null;
  pageRequest: any;
  searchSettings: SearchSettings;
  searchType: AdvancesdSearchResquest['searchType'];
}

export const requestGenerator = ({
  businessType,
  countryCode,
  dealNumber,
  pageRequest,
  searchSettings,
  searchType,
}: RequestGeneratorOptions): Record<string, string> => {
  const request: Record<string, string> = {};
  request.page = pageRequest.page;
  request.pageSize = pageRequest.pageSize;
  request.countryCode = countryCode as string;
  request.searchType = searchType;
  request.businessType = businessType as string;
  request.initialLoad = 'Yes';

  if (dealNumber) {
    request.dealNumberPrefix = dealNumber;
    request.initialLoad = 'No';
    return request;
  }

  Object.values(searchSettings).forEach((data) => {
    data.forEach((d) => {
      request.initialLoad = 'No';
      switch (d.operator) {
        case 'is':
          request[`${d.filterOption}`] = dayjs(d.value).format('YYYY-MM-DD');
          break;
        case 'range':
          request[
            `min${d.filterOption.replace(
              /^./,
              d.filterOption[0].toUpperCase()
            )}`
          ] = dayjs(d.value.starts).format('YYYY-MM-DD');
          request[
            `max${d.filterOption.replace(
              /^./,
              d.filterOption[0].toUpperCase()
            )}`
          ] = dayjs(d.value.ends).format('YYYY-MM-DD');
          break;
        case 'Exact':
          request[`${d.filterOption}`] = d.value;
          break;
        default:
          request[`${d.filterOption}${d.operator ?? ''}`] = d.value;
          break;
      }
    });
  });
  return request;
};

export type V2RecordResponse = {
  acopeuid: string;
  biduploaddate: string;
  business_type: string;
  customerseT_ID: number;
  deal_display_number: string;
  deal_expiry: string;
  deal_id: string | null;
  deal_ingested_on: string;
  deal_number: string;
  deal_start: string;
  enduser_address: string;
  enduser_name: string;
  euaffiliationcount: string;
  id: number;
  importreferenceid: number;
  price_protection_expiry: string;
  reseller_name: string;
  reseller_number: string;
  sbo_currencycode: string;
  totaldiscountline: number;
  vendor_id: number;
  vendor_name: string;
  vendor_number: string;
  vendor_program_id: number;
  vendor_program_name: string;
  version_number: string;
};

export type AdvancesdSearcV2hResponse = {
  dealsV2: V2RecordResponse[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
};

export function useAdvancedSearchV2Query(
  params:
    | (Partial<AdvancesdSearchResquest> & PageRequest)
    | Record<string, string>
) {
  const buildVars = useBuildVars();
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode');
  return useApiQuery<AdvancesdSearcV2hResponse>(
    '/system.webapi.gds/api/v1/GdsCoreApiDeals/advancedSearchV2',
    { countryCode, ...params },
    { enabled: Boolean(params.countryCode ?? countryCode) },
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}
export function useAdvancedSearchV2QueryBusinessTypes(
  params:
    | (Partial<AdvancesdSearchResquest> & PageRequest)
    | Record<string, string>
) {
  const buildVars = useBuildVars();
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode');
  return useApiQuery<AdvancesdSearcV2hResponse>(
    '/system.webapi.gds/api/v1/GdsCoreApiDeals/SimpleSearchAcrossBusinessTypes',
    { countryCode, ...params },
    { enabled: Boolean(params.countryCode ?? countryCode) },
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}
